import { HighlightOff } from "@mui/icons-material";
import {
  Modal,
  Card,
  CardHeader,
  CardContent,
  Box,
  IconButton,
  Typography,
  Button,
  Grid,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { modalStyle } from "../../../../_utils/modalStyle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import CustomButtonModal from "../../../common/CustomBtnModal";
import { useTranslation } from "react-i18next";
import { ProfileContext } from "../../../../contexts/ProfileContext";

const parseSectors = (sectors: any[] | string) => {
  if (!sectors) return [];
  if (typeof sectors === "string") {
    return JSON.parse(sectors);
  }
  return sectors;
};

const ModalEditSectors: React.FC<any> = ({
  personId,
  open,
  onClose,
  additionalInfo,
  sectors = [],
  sectorsLoading,
  onSave,
}) => {
  const ctx = useContext(ProfileContext);

  const { setProfileSectors } = ctx ?? {};

  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const { t } = useTranslation("common");

  useEffect(() => {
    if (open && additionalInfo?.sectors) {
      setSelectedSectors(parseSectors(additionalInfo.sectors));
    }
  }, [open, additionalInfo]);

  useEffect(() => {
    if (open) {
      setSelectedSectors(parseSectors(additionalInfo?.sectors));
    }
  }, [open, additionalInfo?.sectors]);

  const handleToggle = (sector: {
    label: string;
    image: string;
    id: string;
  }) => {
    setSelectedSectors((prev) =>
      prev.some((item) => item === sector.id)
        ? prev.filter((item) => item !== sector.id)
        : [...prev, sector.id]
    );
  };

  const handleSave = async () => {
    if (onSave) {
      onSave(selectedSectors);
      onClose();
      return;
    }
    try {
      await axios.put(`/people/${personId}`, {
        otherData: {
          ...additionalInfo?.otherData,
          additionalInfo: {
            ...additionalInfo,
            sectors: selectedSectors
              ?.map((sector) => sector)
              .filter((el) => el),
          },
        },
      });
      setProfileSectors?.(
        selectedSectors.map((sector) => sector).filter((el) => el)
      );
      if (!setProfileSectors) {
        additionalInfo.sectors = selectedSectors;
      }
      onClose();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
    }
  };

  const activitySectors: {
    image: string;
    label: string;
    id: string;
  }[] = sectors ?? [];

  return (
    <Modal open={open} onClose={onClose}>
      <Card sx={{ ...modalStyle }}>
        <CardHeader
          title={
            <Box
              sx={{
                display: "flex",
                alignItems: "start",
                mb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{ ml: "32px", textAlign: "center", flexGrow: 1 }}
              >
                {t("profilepage.additional_infos.sectors.title_modal")}
              </Typography>
              <IconButton
                aria-label="close"
                sx={{ mr: -2, mt: -2 }}
                onClick={onClose}
              >
                <HighlightOff />
              </IconButton>
            </Box>
          }
        />
        <CardContent>
          <Box sx={{ height: "100%", width: "100%", overflow: "auto" }}>
            <Grid container spacing={2}>
              {sectorsLoading && <p>{t("splash_screen.loading")}...</p>}
              {!sectorsLoading &&
                activitySectors?.map((el) => {
                  const isSelected = selectedSectors?.some(
                    (item) => item === el.id
                  );
                  return (
                    <Grid
                      key={el.id}
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                        marginBottom: "10px",
                        cursor: "pointer",
                      }}
                      onClick={() => handleToggle(el)}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          width: "100%",
                          maxWidth: "300px",
                          height: "100%",
                          maxHeight: "200px",
                          textAlign: "center",
                        }}
                      >
                        <img
                          style={{
                            width: "100%",
                            maxHeight: "100px",
                            objectFit: "cover",
                            borderTopLeftRadius: "8px",
                            borderTopRightRadius: "8px",
                          }}
                          src={el.image}
                          alt={el.label}
                          draggable="false"
                        />
                        <Grid
                          container
                          alignItems="center"
                          justifyContent="center"
                          sx={{ flexGrow: 1, width: "100%", padding: 1 }}
                        >
                          <Grid
                            item
                            xs={8}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ lineHeight: 1.2, userSelect: "none" }}
                            >
                              {el.label}
                            </Typography>
                          </Grid>
                          <Grid
                            item
                            xs={4}
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Button
                              color="primary"
                              variant={isSelected ? "text" : "contained"}
                              sx={{
                                maxHeight: "30px",
                                height: "30px",
                                maxWidth: "30px",
                                minWidth: "30px",
                                width: "100%",
                                borderRadius: "50px",
                                padding: 0,
                              }}
                            >
                              {isSelected ? (
                                <CheckCircleIcon
                                  color="success"
                                  sx={{
                                    cursor: "pointer",
                                    fontSize: "35px",
                                    bottom: 0,
                                    right: 10,
                                    borderRadius: "50%",
                                    boxShadow: "4px 6px 12px #8CFAC788",
                                  }}
                                />
                              ) : (
                                <AddIcon />
                              )}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  );
                })}
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              padding: 2,
              gap: 2,
            }}
          >
            <CustomButtonModal
              variant="outlined"
              color="secondary"
              text={t("profilepage.additional_infos.other.btn_close")}
              onClick={onClose}
            />
            <CustomButtonModal
              disabled={sectorsLoading}
              variant="contained"
              color="primary"
              text={t("profilepage.additional_infos.other.btn_save")}
              onClick={handleSave}
            />
          </Box>
        </CardContent>
      </Card>
    </Modal>
  );
};

export default ModalEditSectors;
